import React, { useState, useEffect } from 'react';
import { BookOpen, Headphones, Compass, Wrench, Plus, Trash2, Edit2, Pin, PinOff, ScrollText, Users, ChevronDown, ChevronUp, Save, Loader2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import api from '../../lib/api';
import { toast } from 'sonner';
import AdminAltarPresente from '../../components/AdminAltarPresente';
import AdminCalendarioEvents from '../../components/AdminCalendarioEvents';

const CONTENT_TYPES = [
  { value: 'lectura', label: 'Lectura Consciente', icon: BookOpen, color: '#c9a227' },
  { value: 'ajuste', label: 'Ajuste Quincenal', icon: Compass, color: '#8fbf7f' },
  { value: 'meditacion', label: 'Meditación', icon: Headphones, color: '#9a8ec4' },
  { value: 'herramienta', label: 'Herramienta Práctica', icon: Wrench, color: '#d4855a' },
];

const QUESTION_CATEGORIES = [
  { value: 'reflexion', label: 'Reflexión' },
  { value: 'presencia', label: 'Presencia' },
  { value: 'soltar', label: 'Soltar' },
  { value: 'estacional', label: 'Estacional' },
];

const AdminCampus = () => {
  const [tab, setTab] = useState('content');
  const [contents, setContents] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [decretos, setDecretos] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);

  // Forms
  const [showContentForm, setShowContentForm] = useState(false);
  const [editingContent, setEditingContent] = useState(null);
  const [contentForm, setContentForm] = useState({ title: '', content_type: 'lectura', body: '', audio_url: '', month_key: '', order: 0, decreto_id: '', published: true });

  const [showQuestionForm, setShowQuestionForm] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [questionForm, setQuestionForm] = useState({ question: '', category: 'reflexion', is_pinned: false });

  const [showDecretoForm, setShowDecretoForm] = useState(false);
  const [editingDecreto, setEditingDecreto] = useState(null);
  const [decretoForm, setDecretoForm] = useState({ text: '', category: '', month_key: '' });

  const [showSubForm, setShowSubForm] = useState(false);
  const [subForm, setSubForm] = useState({ email: '', plan: 'monthly' });

  // Mi Camino visibility flag (allows hiding the Campus card on Mi Camino)
  const [campusConfig, setCampusConfig] = useState(null);
  const [togglingVisible, setTogglingVisible] = useState(false);
  const [togglingSubs, setTogglingSubs] = useState(false);

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    try {
      const [c, q, d, s, cfg] = await Promise.all([
        api.get('/admin/campus/content'),
        api.get('/admin/campus/questions'),
        api.get('/admin/campus/decretos'),
        api.get('/admin/campus/subscriptions'),
        api.get('/campus/config'),
      ]);
      setContents(c.data.contents || []);
      setQuestions(q.data.questions || []);
      setDecretos(d.data.decretos || []);
      setSubscriptions(s.data.subscriptions || []);
      setCampusConfig(cfg.data || { mi_camino_card_visible: true });
    } catch (err) {
      toast.error('Error cargando datos del Campus');
    } finally {
      setLoading(false);
    }
  };

  const toggleMiCaminoCard = async () => {
    if (togglingVisible || !campusConfig) return;
    const next = !campusConfig.mi_camino_card_visible;
    setTogglingVisible(true);
    try {
      const res = await api.put('/admin/campus/config', { mi_camino_card_visible: next });
      setCampusConfig(res.data.config);
      toast.success(next ? 'Card visible en Mi Camino' : 'Card oculta en Mi Camino');
    } catch {
      toast.error('Error al actualizar');
    } finally {
      setTogglingVisible(false);
    }
  };

  const toggleSubscriptions = async () => {
    if (togglingSubs || !campusConfig) return;
    const next = !campusConfig.subscriptions_enabled;
    if (next && !window.confirm('Vas a abrir los pagos de suscripción al Campus. ¿Está el contenido listo?')) return;
    setTogglingSubs(true);
    try {
      const res = await api.put('/admin/campus/config', { subscriptions_enabled: next });
      setCampusConfig(res.data.config);
      toast.success(next ? 'Suscripciones abiertas — los pagos ya están activos' : 'Suscripciones cerradas — los pagos quedan bloqueados');
    } catch {
      toast.error('Error al actualizar');
    } finally {
      setTogglingSubs(false);
    }
  };

  // ============ CONTENT CRUD ============
  const handleSaveContent = async () => {
    try {
      if (editingContent) {
        await api.put(`/admin/campus/content/${editingContent}`, contentForm);
        toast.success('Contenido actualizado');
      } else {
        await api.post('/admin/campus/content', contentForm);
        toast.success('Contenido creado');
      }
      setShowContentForm(false);
      setEditingContent(null);
      resetContentForm();
      fetchAll();
    } catch { toast.error('Error guardando contenido'); }
  };

  const handleDeleteContent = async (id) => {
    if (!window.confirm('¿Eliminar este contenido?')) return;
    try {
      await api.delete(`/admin/campus/content/${id}`);
      toast.success('Eliminado');
      fetchAll();
    } catch { toast.error('Error'); }
  };

  const resetContentForm = () => {
    const now = new Date();
    setContentForm({ title: '', content_type: 'lectura', body: '', audio_url: '', month_key: `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`, order: 0, decreto_id: '', published: true });
  };

  // ============ QUESTION CRUD ============
  const handleSaveQuestion = async () => {
    try {
      if (editingQuestion) {
        await api.put(`/admin/campus/questions/${editingQuestion}`, questionForm);
        toast.success('Pregunta actualizada');
      } else {
        await api.post('/admin/campus/questions', questionForm);
        toast.success('Pregunta creada');
      }
      setShowQuestionForm(false);
      setEditingQuestion(null);
      setQuestionForm({ question: '', category: 'reflexion', is_pinned: false });
      fetchAll();
    } catch { toast.error('Error'); }
  };

  const handleDeleteQuestion = async (id) => {
    if (!window.confirm('¿Eliminar esta pregunta?')) return;
    try { await api.delete(`/admin/campus/questions/${id}`); toast.success('Eliminada'); fetchAll(); } catch { toast.error('Error'); }
  };

  const handlePinQuestion = async (id) => {
    try { await api.put(`/admin/campus/questions/${id}/pin`); fetchAll(); } catch { toast.error('Error'); }
  };

  // ============ DECRETO CRUD ============
  const handleSaveDecreto = async () => {
    try {
      if (editingDecreto) {
        await api.put(`/admin/campus/decretos/${editingDecreto}`, decretoForm);
        toast.success('Decreto actualizado');
      } else {
        await api.post('/admin/campus/decretos', decretoForm);
        toast.success('Decreto creado');
      }
      setShowDecretoForm(false);
      setEditingDecreto(null);
      setDecretoForm({ text: '', category: '', month_key: '' });
      fetchAll();
    } catch { toast.error('Error'); }
  };

  const handleDeleteDecreto = async (id) => {
    if (!window.confirm('¿Eliminar este decreto?')) return;
    try { await api.delete(`/admin/campus/decretos/${id}`); toast.success('Eliminado'); fetchAll(); } catch { toast.error('Error'); }
  };

  // ============ SUBSCRIPTION ============
  const handleCreateSub = async () => {
    try {
      await api.post('/admin/campus/subscriptions', subForm);
      toast.success('Suscripción creada');
      setShowSubForm(false);
      setSubForm({ email: '', plan: 'monthly' });
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
  };

  const handleCancelSub = async (id) => {
    if (!window.confirm('¿Cancelar esta suscripción?')) return;
    try { await api.put(`/admin/campus/subscriptions/${id}/cancel`); toast.success('Cancelada'); fetchAll(); } catch { toast.error('Error'); }
  };

  const TABS = [
    { id: 'content', label: 'Contenido', count: contents.length },
    { id: 'altar', label: 'Altar', count: null },
    { id: 'calendario', label: 'Calendario', count: null },
    { id: 'espacios', label: 'Espacios', count: null },
    { id: 'questions', label: 'Preguntas', count: questions.length },
    { id: 'decretos', label: 'Decretos', count: decretos.length },
    { id: 'subs', label: 'Suscripciones', count: subscriptions.length },
  ];

  if (loading) return <div className="flex justify-center py-12"><div className="w-6 h-6 border-2 border-[#c9a227] border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="space-y-6" data-testid="admin-campus">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-heading text-[hsl(var(--text-primary))]">Campus de Interioridad</h2>
      </div>

      {/* Visibility toggle — controls the Campus card on Mi Camino */}
      {campusConfig && (
        <div className="bg-white border border-gray-100 rounded-xl p-4 flex items-center justify-between gap-4" data-testid="campus-visibility-toggle">
          <div>
            <p className="text-sm font-medium text-[hsl(var(--text-primary))]">
              Card "Campus de Interioridad" en <span className="text-[#c9a227]">Mi Camino</span>
            </p>
            <p className="text-xs text-[hsl(var(--text-tertiary))] mt-1">
              Mientras esté oculta, las alumnas no verán el bloque dorado de invitación al campus en su panel. Puedes reactivarla cuando quieras.
            </p>
          </div>
          <button
            type="button"
            onClick={toggleMiCaminoCard}
            disabled={togglingVisible}
            className="flex items-center gap-2 transition-opacity disabled:opacity-60 flex-shrink-0"
            data-testid="toggle-campus-mi-camino"
          >
            {campusConfig.mi_camino_card_visible ? (
              <>
                <span className="w-12 h-7 rounded-full bg-green-500 relative">
                  <span className="absolute right-1 top-1 w-5 h-5 bg-white rounded-full shadow-sm" />
                </span>
                <span className="text-xs font-medium text-green-700">Visible</span>
              </>
            ) : (
              <>
                <span className="w-12 h-7 rounded-full bg-gray-300 relative">
                  <span className="absolute left-1 top-1 w-5 h-5 bg-white rounded-full shadow-sm" />
                </span>
                <span className="text-xs font-medium text-gray-500">Oculta</span>
              </>
            )}
          </button>
        </div>
      )}

      {/* Subscriptions kill-switch — controls whether PayPal subscriptions can be created */}
      {campusConfig && (
        <div
          className={`rounded-xl p-4 flex items-center justify-between gap-4 border ${
            campusConfig.subscriptions_enabled
              ? 'bg-green-50/50 border-green-200'
              : 'bg-amber-50/50 border-amber-200'
          }`}
          data-testid="campus-subscriptions-toggle"
        >
          <div>
            <p className="text-sm font-medium text-[hsl(var(--text-primary))]">
              Suscripciones de pago al Campus
              {!campusConfig.subscriptions_enabled && (
                <span className="ml-2 inline-flex items-center text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-amber-100 text-amber-800">
                  Cerradas
                </span>
              )}
            </p>
            <p className="text-xs text-[hsl(var(--text-tertiary))] mt-1 max-w-2xl">
              Mientras esté <strong>cerrado</strong>, nadie podrá pagar la suscripción mensual o anual (los botones de PayPal se reemplazan por un aviso de "Próximamente"). Las suscripciones ya activas siguen funcionando con normalidad. Ábrelo solo cuando el contenido esté listo.
            </p>
          </div>
          <button
            type="button"
            onClick={toggleSubscriptions}
            disabled={togglingSubs}
            className="flex items-center gap-2 transition-opacity disabled:opacity-60 flex-shrink-0"
            data-testid="toggle-campus-subscriptions"
          >
            {campusConfig.subscriptions_enabled ? (
              <>
                <span className="w-12 h-7 rounded-full bg-green-500 relative">
                  <span className="absolute right-1 top-1 w-5 h-5 bg-white rounded-full shadow-sm" />
                </span>
                <span className="text-xs font-medium text-green-700">Abiertas</span>
              </>
            ) : (
              <>
                <span className="w-12 h-7 rounded-full bg-amber-400 relative">
                  <span className="absolute left-1 top-1 w-5 h-5 bg-white rounded-full shadow-sm" />
                </span>
                <span className="text-xs font-medium text-amber-700">Cerradas</span>
              </>
            )}
          </button>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
        {TABS.map(t => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`flex-1 px-3 py-2 rounded-md text-xs font-medium transition-all ${tab === t.id ? 'bg-white shadow-sm text-[hsl(var(--text-primary))]' : 'text-gray-500 hover:text-gray-700'}`}
            data-testid={`campus-tab-${t.id}`}
          >
            {t.label}{t.count !== null ? ` (${t.count})` : ''}
          </button>
        ))}
      </div>

      {/* ============ CONTENT TAB ============ */}
      {tab === 'altar' && <AdminAltarPresente />}

      {tab === 'calendario' && (
        <div className="bg-white rounded-xl p-6 border border-gray-100">
          <h3 className="text-base font-medium text-[#5c4a2a] mb-1">Calendario Energético</h3>
          <p className="text-xs text-[#8b7340]/40 mb-4">Las lunas, portales y sabbats son automáticos. Añade aquí tus eventos especiales por mes.</p>
          <AdminCalendarioEvents />
        </div>
      )}

      {tab === 'espacios' && <AdminSacredSpaces />}

      {tab === 'content' && (
        <div>
          <div className="flex justify-between items-center mb-4">
            <p className="text-sm text-gray-500">Lecturas, ajustes, meditaciones y herramientas mensuales</p>
            <Button size="sm" onClick={() => { resetContentForm(); setEditingContent(null); setShowContentForm(true); }} data-testid="add-content-btn">
              <Plus className="w-4 h-4 mr-1" /> Añadir
            </Button>
          </div>

          {showContentForm && (
            <div className="bg-[#FDFBF7] border rounded-xl p-5 mb-4 space-y-3" data-testid="content-form">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">Título</label>
                  <Input value={contentForm.title} onChange={e => setContentForm(f => ({ ...f, title: e.target.value }))} placeholder="Lectura de Marzo..." data-testid="content-title-input" />
                </div>
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">Tipo</label>
                  <select value={contentForm.content_type} onChange={e => setContentForm(f => ({ ...f, content_type: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm" data-testid="content-type-select">
                    {CONTENT_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">Mes (YYYY-MM)</label>
                  <Input value={contentForm.month_key} onChange={e => setContentForm(f => ({ ...f, month_key: e.target.value }))} placeholder="2026-03" data-testid="content-month-input" />
                </div>
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">Orden</label>
                  <Input type="number" value={contentForm.order} onChange={e => setContentForm(f => ({ ...f, order: parseInt(e.target.value) || 0 }))} />
                </div>
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">Decreto vinculado</label>
                  <select value={contentForm.decreto_id} onChange={e => setContentForm(f => ({ ...f, decreto_id: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm">
                    <option value="">Ninguno</option>
                    {decretos.map(d => <option key={d.id} value={d.id}>{d.text.substring(0, 40)}...</option>)}
                  </select>
                </div>
              </div>
              {contentForm.content_type === 'meditacion' && (
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">URL del audio (Google Drive)</label>
                  <Input value={contentForm.audio_url} onChange={e => setContentForm(f => ({ ...f, audio_url: e.target.value }))} placeholder="https://drive.google.com/file/d/..." data-testid="content-audio-input" />
                </div>
              )}
              <div>
                <label className="text-xs text-gray-500 mb-1 block">Contenido (Markdown)</label>
                <textarea
                  value={contentForm.body}
                  onChange={e => setContentForm(f => ({ ...f, body: e.target.value }))}
                  rows={8}
                  className="w-full border rounded-lg px-3 py-2 text-sm font-mono resize-y"
                  placeholder="# Título&#10;&#10;Texto del contenido en Markdown..."
                  data-testid="content-body-input"
                />
              </div>
              <div className="flex items-center gap-3">
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={contentForm.published} onChange={e => setContentForm(f => ({ ...f, published: e.target.checked }))} /> Publicado
                </label>
              </div>
              <div className="flex gap-2">
                <Button size="sm" onClick={handleSaveContent} data-testid="save-content-btn">{editingContent ? 'Actualizar' : 'Crear'}</Button>
                <Button size="sm" variant="outline" onClick={() => { setShowContentForm(false); setEditingContent(null); }}>Cancelar</Button>
              </div>
            </div>
          )}

          <div className="space-y-2">
            {contents.map(c => {
              const typeConf = CONTENT_TYPES.find(t => t.value === c.content_type) || CONTENT_TYPES[0];
              const Icon = typeConf.icon;
              return (
                <div key={c.id} className="flex items-center gap-3 bg-white border rounded-lg px-4 py-3">
                  <Icon className="w-4 h-4 flex-shrink-0" style={{ color: typeConf.color }} />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{c.title}</p>
                    <p className="text-xs text-gray-400">{c.month_key} · {typeConf.label} {!c.published && '· (borrador)'}</p>
                  </div>
                  <button onClick={() => { setContentForm({ ...c }); setEditingContent(c.id); setShowContentForm(true); }} className="text-gray-400 hover:text-gray-600"><Edit2 className="w-4 h-4" /></button>
                  <button onClick={() => handleDeleteContent(c.id)} className="text-gray-400 hover:text-red-500"><Trash2 className="w-4 h-4" /></button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ============ QUESTIONS TAB ============ */}
      {tab === 'questions' && (
        <div>
          <div className="flex justify-between items-center mb-4">
            <p className="text-sm text-gray-500">Preguntas sagradas del umbral</p>
            <Button size="sm" onClick={() => { setQuestionForm({ question: '', category: 'reflexion', is_pinned: false }); setEditingQuestion(null); setShowQuestionForm(true); }} data-testid="add-question-btn">
              <Plus className="w-4 h-4 mr-1" /> Añadir
            </Button>
          </div>

          {showQuestionForm && (
            <div className="bg-[#FDFBF7] border rounded-xl p-5 mb-4 space-y-3" data-testid="question-form">
              <div>
                <label className="text-xs text-gray-500 mb-1 block">Pregunta</label>
                <textarea value={questionForm.question} onChange={e => setQuestionForm(f => ({ ...f, question: e.target.value }))} rows={2} className="w-full border rounded-lg px-3 py-2 text-sm resize-none" placeholder="¿Qué necesita tu cuerpo en este momento?" data-testid="question-text-input" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">Categoría</label>
                  <select value={questionForm.category} onChange={e => setQuestionForm(f => ({ ...f, category: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm">
                    {QUESTION_CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                  </select>
                </div>
                <div className="flex items-end">
                  <label className="flex items-center gap-2 text-sm">
                    <input type="checkbox" checked={questionForm.is_pinned} onChange={e => setQuestionForm(f => ({ ...f, is_pinned: e.target.checked }))} /> Fijar como pregunta actual
                  </label>
                </div>
              </div>
              <div className="flex gap-2">
                <Button size="sm" onClick={handleSaveQuestion} data-testid="save-question-btn">{editingQuestion ? 'Actualizar' : 'Crear'}</Button>
                <Button size="sm" variant="outline" onClick={() => { setShowQuestionForm(false); setEditingQuestion(null); }}>Cancelar</Button>
              </div>
            </div>
          )}

          <div className="space-y-2">
            {questions.map(q => (
              <div key={q.id} className={`flex items-center gap-3 bg-white border rounded-lg px-4 py-3 ${q.is_pinned ? 'border-[#c9a227]/30 bg-[#FDFBF7]' : ''}`}>
                <div className="flex-1 min-w-0">
                  <p className="text-sm">{q.question}</p>
                  <p className="text-xs text-gray-400 mt-0.5">{q.category} {q.is_pinned && '· Fijada'}</p>
                </div>
                <button onClick={() => handlePinQuestion(q.id)} className={`${q.is_pinned ? 'text-[#c9a227]' : 'text-gray-300 hover:text-gray-500'}`} title={q.is_pinned ? 'Desfijar' : 'Fijar'}>
                  {q.is_pinned ? <Pin className="w-4 h-4" /> : <PinOff className="w-4 h-4" />}
                </button>
                <button onClick={() => { setQuestionForm({ question: q.question, category: q.category, is_pinned: q.is_pinned }); setEditingQuestion(q.id); setShowQuestionForm(true); }} className="text-gray-400 hover:text-gray-600"><Edit2 className="w-4 h-4" /></button>
                <button onClick={() => handleDeleteQuestion(q.id)} className="text-gray-400 hover:text-red-500"><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ============ DECRETOS TAB ============ */}
      {tab === 'decretos' && (
        <div>
          <div className="flex justify-between items-center mb-4">
            <p className="text-sm text-gray-500">Decretos desbloqueables al leer contenido</p>
            <Button size="sm" onClick={() => { setDecretoForm({ text: '', category: '', month_key: new Date().toISOString().slice(0, 7) }); setEditingDecreto(null); setShowDecretoForm(true); }} data-testid="add-decreto-btn">
              <Plus className="w-4 h-4 mr-1" /> Añadir
            </Button>
          </div>

          {showDecretoForm && (
            <div className="bg-[#FDFBF7] border rounded-xl p-5 mb-4 space-y-3" data-testid="decreto-form">
              <div>
                <label className="text-xs text-gray-500 mb-1 block">Texto del decreto</label>
                <textarea value={decretoForm.text} onChange={e => setDecretoForm(f => ({ ...f, text: e.target.value }))} rows={2} className="w-full border rounded-lg px-3 py-2 text-sm resize-none" placeholder="Yo soy la calma que sostiene..." data-testid="decreto-text-input" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">Categoría</label>
                  <Input value={decretoForm.category} onChange={e => setDecretoForm(f => ({ ...f, category: e.target.value }))} placeholder="sostén, presencia..." />
                </div>
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">Mes</label>
                  <Input value={decretoForm.month_key} onChange={e => setDecretoForm(f => ({ ...f, month_key: e.target.value }))} placeholder="2026-03" />
                </div>
              </div>
              <div className="flex gap-2">
                <Button size="sm" onClick={handleSaveDecreto} data-testid="save-decreto-btn">{editingDecreto ? 'Actualizar' : 'Crear'}</Button>
                <Button size="sm" variant="outline" onClick={() => { setShowDecretoForm(false); setEditingDecreto(null); }}>Cancelar</Button>
              </div>
            </div>
          )}

          <div className="space-y-2">
            {decretos.map(d => (
              <div key={d.id} className="flex items-center gap-3 bg-white border rounded-lg px-4 py-3">
                <ScrollText className="w-4 h-4 text-[#c9a227] flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm italic">"{d.text}"</p>
                  <p className="text-xs text-gray-400 mt-0.5">{d.month_key} · {d.category}</p>
                </div>
                <button onClick={() => { setDecretoForm({ text: d.text, category: d.category, month_key: d.month_key }); setEditingDecreto(d.id); setShowDecretoForm(true); }} className="text-gray-400 hover:text-gray-600"><Edit2 className="w-4 h-4" /></button>
                <button onClick={() => handleDeleteDecreto(d.id)} className="text-gray-400 hover:text-red-500"><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ============ SUBSCRIPTIONS TAB ============ */}
      {tab === 'subs' && (
        <div>
          <div className="flex justify-between items-center mb-4">
            <p className="text-sm text-gray-500">Suscriptoras con acceso al Campus</p>
            <Button size="sm" onClick={() => { setSubForm({ email: '', plan: 'monthly' }); setShowSubForm(true); }} data-testid="add-sub-btn">
              <Plus className="w-4 h-4 mr-1" /> Dar acceso
            </Button>
          </div>

          {showSubForm && (
            <div className="bg-[#FDFBF7] border rounded-xl p-5 mb-4 space-y-3" data-testid="sub-form">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">Email de la alumna</label>
                  <Input value={subForm.email} onChange={e => setSubForm(f => ({ ...f, email: e.target.value }))} placeholder="alumna@email.com" data-testid="sub-email-input" />
                </div>
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">Plan</label>
                  <select value={subForm.plan} onChange={e => setSubForm(f => ({ ...f, plan: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm">
                    <option value="monthly">Mensual (22€/mes)</option>
                    <option value="annual">Anual (220€/año)</option>
                  </select>
                </div>
              </div>
              <div className="flex gap-2">
                <Button size="sm" onClick={handleCreateSub} data-testid="save-sub-btn">Dar acceso</Button>
                <Button size="sm" variant="outline" onClick={() => setShowSubForm(false)}>Cancelar</Button>
              </div>
            </div>
          )}

          <div className="space-y-2">
            {subscriptions.length === 0 ? (
              <p className="text-sm text-gray-400 text-center py-8">No hay suscripciones activas</p>
            ) : (
              subscriptions.map(s => (
                <div key={s.id} className="flex items-center gap-3 bg-white border rounded-lg px-4 py-3">
                  <Users className="w-4 h-4 text-gray-400 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium">{s.user?.name || s.user?.email || s.user_id}</p>
                    <p className="text-xs text-gray-400">{s.plan === 'annual' ? 'Anual' : 'Mensual'} · {s.status} · {s.payment_method}</p>
                  </div>
                  {s.status === 'active' && (
                    <button onClick={() => handleCancelSub(s.id)} className="text-xs text-red-400 hover:text-red-600">Cancelar</button>
                  )}
                  {s.status === 'cancelled' && (
                    <span className="text-xs text-gray-400">Cancelada</span>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

/* ═══ ADMIN SACRED SPACES ═══ */
const SPACE_LABELS = {
  templo_corazon: 'Cámara Sagrada del Corazón',
  nucleo_gaia: 'Núcleo Cristalino de Gaia',
};

const AdminSacredSpaces = () => {
  const [spaces, setSpaces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ title: '', text: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => { loadSpaces(); }, []);

  const loadSpaces = async () => {
    try {
      const res = await api.get('/admin/campus/sacred-spaces');
      setSpaces(res.data.spaces || []);
    } catch { toast.error('Error al cargar espacios sagrados'); }
    setLoading(false);
  };

  const startEdit = (space) => {
    setEditing(space.space_id);
    setForm({ title: space.title || '', text: space.text || '' });
  };

  const saveSpace = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      await api.put(`/admin/campus/sacred-spaces/${editing}`, form);
      toast.success('Texto sagrado actualizado');
      setEditing(null);
      loadSpaces();
    } catch { toast.error('Error al guardar'); }
    setSaving(false);
  };

  if (loading) return <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-[#c9a227]" /></div>;

  return (
    <div className="space-y-4" data-testid="admin-sacred-spaces">
      <p className="text-xs text-[#8b7340]/50">Textos sagrados que aparecen en la pestaña desplegable de cada espacio contemplativo</p>
      {spaces.map(space => (
        <div key={space.space_id} className="border border-[#e8e2d8] rounded-xl bg-white overflow-hidden">
          <div className="flex items-center justify-between px-5 py-4 border-b border-[#e8e2d8]/60">
            <div>
              <h3 className="text-sm font-medium text-[#5c4a2a]">{SPACE_LABELS[space.space_id] || space.space_id}</h3>
              <p className="text-[10px] text-[#8b7340]/40 mt-0.5">{space.updated_at ? `Actualizado: ${new Date(space.updated_at).toLocaleDateString('es-ES')}` : ''}</p>
            </div>
            {editing !== space.space_id && (
              <button onClick={() => startEdit(space)}
                className="flex items-center gap-1.5 text-xs text-[#c9a227] hover:text-[#8b6914] px-3 py-1.5 rounded-lg border border-[#c9a227]/20 hover:bg-[#c9a227]/5 transition-all"
                data-testid={`edit-space-${space.space_id}`}
              >
                <Edit2 className="w-3 h-3" /> Editar
              </button>
            )}
          </div>

          {editing === space.space_id ? (
            <div className="p-5 space-y-4">
              <div>
                <label className="text-[10px] uppercase tracking-wider text-[#8b7340]/50">Título</label>
                <input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })}
                  className="w-full mt-1 text-sm border border-[#e8e2d8] rounded-lg px-3 py-2 focus:outline-none focus:border-[#c9a227]/40 text-[#5c4a2a]" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-[#8b7340]/50">Texto (usa **texto** para negritas, — para listas)</label>
                <textarea value={form.text} onChange={e => setForm({ ...form, text: e.target.value })}
                  rows={16}
                  className="w-full mt-1 text-sm border border-[#e8e2d8] rounded-lg px-3 py-2 focus:outline-none focus:border-[#c9a227]/40 text-[#5c4a2a] resize-y leading-relaxed font-light" />
              </div>
              <div className="flex gap-2">
                <button onClick={saveSpace} disabled={saving}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-medium bg-[#c9a227]/10 text-[#8b6914] hover:bg-[#c9a227]/20 border border-[#c9a227]/20 transition-all"
                  data-testid={`save-space-${editing}`}
                >
                  {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
                  Guardar
                </button>
                <button onClick={() => setEditing(null)}
                  className="px-4 py-2 rounded-lg text-xs text-[#8b7340]/50 hover:text-[#5c4a2a] border border-[#e8e2d8] transition-all">
                  Cancelar
                </button>
              </div>
            </div>
          ) : (
            <div className="px-5 py-4 max-h-48 overflow-y-auto">
              <p className="text-xs text-[#5c4a2a]/60 whitespace-pre-line leading-relaxed font-light">{space.text?.substring(0, 300)}{space.text?.length > 300 ? '...' : ''}</p>
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

export default AdminCampus;
